import React, { useState, useEffect } from 'react';
import { X, Save, MapPin } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface AOICreatorProps {
  onSave: (aoi: {
    name: string;
    description?: string;
    geometry: {
      type: string;
      coordinates: number[][][];
    };
  }) => void;
  onCancel: () => void;
  mapRef?: any;
  onDrawingModeChange?: (mode: 'rectangle' | 'polygon' | null) => void;
  onGeometryDrawn?: (geometry: { type: string; coordinates: number[][][] }) => void;
  drawnGeometry?: { type: string; coordinates: number[][][] } | null;
}

const AOICreator: React.FC<AOICreatorProps> = ({ onSave, onCancel, onDrawingModeChange, drawnGeometry: externalGeometry }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [drawingMode, setDrawingMode] = useState<'rectangle' | 'polygon' | null>(null);
  const [drawnGeometry, setDrawnGeometry] = useState<{ type: string; coordinates: number[][][] } | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (onDrawingModeChange) {
      onDrawingModeChange(drawingMode);
    }
  }, [drawingMode, onDrawingModeChange]);

  useEffect(() => {
    if (externalGeometry) {
      console.log('AOICreator: Received external geometry:', externalGeometry);
      setDrawnGeometry(externalGeometry);
      setIsDrawing(false);
    }
  }, [externalGeometry]);

  const handleDrawRectangle = () => {
    if (drawingMode === 'rectangle') {
      setDrawingMode(null);
      setIsDrawing(false);
    } else {
      setDrawingMode('rectangle');
      setIsDrawing(true);
      setDrawnGeometry(null);
    }
  };

  const handleDrawPolygon = () => {
    if (drawingMode === 'polygon') {
      setDrawingMode(null);
      setIsDrawing(false);
    } else {
      setDrawingMode('polygon');
      setIsDrawing(true);
      setDrawnGeometry(null);
    }
  };


  const handleSave = () => {
    if (!name.trim()) {
      toast({
        title: 'Name required',
        description: 'Please enter a name for the AOI',
        variant: 'destructive',
      });
      return;
    }

    if (!drawnGeometry || !drawnGeometry.coordinates || drawnGeometry.coordinates.length === 0) {
      toast({
        title: 'No area drawn',
        description: 'Please draw an area on the map',
        variant: 'destructive',
      });
      return;
    }
    
    onSave({
      name: name.trim(),
      description: description.trim() || undefined,
      geometry: drawnGeometry
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-4" style={{ pointerEvents: 'auto' }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <MapPin className="w-5 h-5" />
          Create New AOI
        </h3>
        <button
          onClick={onCancel}
          className="p-1 hover:bg-muted rounded transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter AOI name"
            className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter AOI description (optional)"
            rows={3}
            className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Drawing Tool
          </label>
          <div className="flex gap-2">
            <button
              onClick={handleDrawRectangle}
              className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                drawingMode === 'rectangle'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              Rectangle
            </button>
            <button
              onClick={handleDrawPolygon}
              className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                drawingMode === 'polygon'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              Polygon
            </button>
          </div>
          {isDrawing && (
            <p className="text-xs text-muted-foreground mt-2">
              {drawingMode === 'rectangle' 
                ? 'Click and drag on the map to draw a rectangle'
                : 'Click on the map to add points, double-click to finish'}
            </p>
          )}
          {drawnGeometry && !isDrawing && (
            <div className="p-2 bg-green-500/10 border border-green-500/20 rounded text-xs text-green-700 mt-2">
              ✓ Area drawn successfully
            </div>
          )}
        </div>

        <div className="flex gap-2 pt-2">
          <button
            onClick={handleSave}
            disabled={!name.trim() || !drawnGeometry}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground text-primary-foreground rounded-md text-sm font-medium transition-colors"
          >
            <Save className="w-4 h-4" />
            Save AOI
          </button>
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-muted hover:bg-muted/80 text-muted-foreground rounded-md text-sm font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AOICreator;

